import { useSeatEligiblePassengers, filterDateRangesByNights, computeBundleDataForOffer, resolveFlightPriceForFareClass, resolveOriginalFlightPriceForFareClass, allocatePackagesForDate } from './utils'
import { useContext, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import { fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { FlightsClassTypes } from 'constants/flight'
import { getCharterSelectableCashCabins } from 'lib/maldivesCharter/charterCabins'
import { useBundleFlightData } from './useBundleFlightData'
import type { DateRange, CalendarItemBundleData, FlightDataByDate, ResolvedFlightInfo } from './types'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { getCharterFareClassPolicy, resolveAllowedFareClass } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'

/** Hotel-only minimum for a (cabin, date) — surfaced so each per-cabin column in the
 *  desktop trip-options table can read the lowest hotel for offers that allow that
 *  cabin, independent of whatever cabin is selected at the page level. */
export interface MaldivesCabinHotelEntry {
  hotelPrice?: number
  hotelMemberPrice?: number
  hotelValue?: number
}

export interface MaldivesOffersPricingResult {
  /** Aggregate pricing by date - lowest price across all offers for each date */
  calendarPricingByDate: Record<string, CalendarItemBundleData>
  /** Per-cabin lowest hotel by date. Each cabin only sees offers whose `campaignTags`
   *  permit it; an unrestricted offer feeds every cabin. */
  hotelPriceByCabinAndDate: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  isLoading: boolean
}

type FlightDataByFareClassMap = Partial<Record<FlightsClassTypes, Record<string, FlightDataByDate>>>

function useFlightDataByFareClass(params: {
  rooms: Array<App.Occupants>
  dateRangesByMonth: Record<string, Array<DateRange>>
  searchFareClass: FlightsClassTypes
  currency: string
  charterBundleRoute?: App.CharterBundleRoute
  restrictedFareClassesNeeded: Set<FlightsClassTypes>
  seatEligiblePassengers: number
  feederOriginAirportCode?: string
}): {
  flightDataByFareClass: FlightDataByFareClassMap
  isFlightLoading: boolean
} {
  const {
    rooms,
    dateRangesByMonth,
    searchFareClass,
    currency,
    charterBundleRoute,
    restrictedFareClassesNeeded,
    seatEligiblePassengers,
    feederOriginAirportCode,
  } = params

  const { flightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth,
    fareClass: searchFareClass,
    currency,
    charterBundleRoute,
    seatEligiblePassengers,
    feederOriginAirportCode,
  })
  const shouldFetchPremium = restrictedFareClassesNeeded.has(FlightsClassTypes.PREMIUM)
  const shouldFetchBusiness = restrictedFareClassesNeeded.has(FlightsClassTypes.BUSINESS)
  const shouldFetchLuxRow = restrictedFareClassesNeeded.has(FlightsClassTypes.LUXROW)
  const { flightDataByDate: premiumFlightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth,
    fareClass: FlightsClassTypes.PREMIUM,
    currency,
    charterBundleRoute,
    disabled: !shouldFetchPremium,
    seatEligiblePassengers,
    feederOriginAirportCode,
  })
  const { flightDataByDate: businessFlightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth,
    fareClass: FlightsClassTypes.BUSINESS,
    currency,
    charterBundleRoute,
    disabled: !shouldFetchBusiness,
    seatEligiblePassengers,
    feederOriginAirportCode,
  })
  const { flightDataByDate: luxRowFlightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth,
    fareClass: FlightsClassTypes.LUXROW,
    currency,
    charterBundleRoute,
    disabled: !shouldFetchLuxRow,
    seatEligiblePassengers,
    feederOriginAirportCode,
  })

  const isFlightLoading = Object.values(flightDataByDate).some(d => d.fetching) ||
    (shouldFetchPremium && Object.values(premiumFlightDataByDate).some(d => d.fetching)) ||
    (shouldFetchBusiness && Object.values(businessFlightDataByDate).some(d => d.fetching)) ||
    (shouldFetchLuxRow && Object.values(luxRowFlightDataByDate).some(d => d.fetching))
  const flightDataByFareClass = useMemo<FlightDataByFareClassMap>(() => ({
    [searchFareClass]: flightDataByDate,
    ...(shouldFetchPremium && searchFareClass !== FlightsClassTypes.PREMIUM
      ? { [FlightsClassTypes.PREMIUM]: premiumFlightDataByDate }
      : {}),
    ...(shouldFetchBusiness && searchFareClass !== FlightsClassTypes.BUSINESS
      ? { [FlightsClassTypes.BUSINESS]: businessFlightDataByDate }
      : {}),
    ...(shouldFetchLuxRow && searchFareClass !== FlightsClassTypes.LUXROW
      ? { [FlightsClassTypes.LUXROW]: luxRowFlightDataByDate }
      : {}),
  }), [
    searchFareClass,
    flightDataByDate,
    premiumFlightDataByDate,
    businessFlightDataByDate,
    luxRowFlightDataByDate,
    shouldFetchPremium,
    shouldFetchBusiness,
    shouldFetchLuxRow,
  ])

  return {
    flightDataByFareClass,
    isFlightLoading,
  }
}

interface UseMaldivesOffersPricingParams {
  offers: Array<App.HotelOffer>
  rooms: Array<App.Occupants>
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedFareClass?: FlightsClassTypes
  searchFareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  nights: number
  feederOriginAirportCode?: string
}

/**
 * Hook to calculate pricing for all offers on the landing page.
 * This fetches calendar data for all offers and computes optimal room allocation.
 */
export function useMaldivesOffersPricing(params: UseMaldivesOffersPricingParams): MaldivesOffersPricingResult {
  const {
    offers,
    rooms,
    dateRangesByMonth,
    selectedFareClass,
    searchFareClass,
    charterBundleRoute,
    nights,
    feederOriginAirportCode,
  } = params

  const dispatch = useAppDispatch()
  const currencyContext = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const currency: string = currencyContext || 'AUD'
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)
  const isLuxRowEnabled = useFeatureFlag(OptimizelyFeatureFlags.luxRow)
  const selectableCashCabins = useMemo(
    () => getCharterSelectableCashCabins(isLuxRowEnabled),
    [isLuxRowEnabled],
  )
  const fareClassPolicyByOfferId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof getCharterFareClassPolicy>>()
    offers.forEach((offer) => {
      map.set(offer.id, getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      }))
    })
    return map
  }, [offers, rooms])
  const restrictedFareClassesNeeded = useMemo(() => {
    const needed = new Set<FlightsClassTypes>()
    if (selectedFareClass) {
      return needed
    }
    offers.forEach((offer) => {
      const policy = fareClassPolicyByOfferId.get(offer.id)
      if (!policy || policy.taggedFareClasses.length === 0) {
        return
      }
      const resolved = resolveAllowedFareClass({
        selectedFareClass,
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      if (resolved) {
        needed.add(resolved)
      }
    })
    return needed
  }, [fareClassPolicyByOfferId, offers, rooms, selectedFareClass])

  const filteredDateRangesByMonth = useMemo(
    () => filterDateRangesByNights(dateRangesByMonth, nights),
    [dateRangesByMonth, nights],
  )

  const offerPackagesMap = useMemo(() => {
    const map: Record<string, Array<App.HotelPackage>> = {}
    for (const offer of offers) {
      map[offer.id] = offer.packages.filter(pkg => pkg.duration === nights)
    }
    return map
  }, [offers, nights])

  useEffect(() => {
    if (rooms.length === 0 || offers.length === 0) return

    for (const offer of offers) {
      const packages = offerPackagesMap[offer.id]
      if (packages.length === 0) continue

      dispatch(fetchCalendarsByOccupancy(offer.id, {
        occupants: rooms,
        packages,
      }))
    }
  }, [dispatch, offers, rooms, offerPackagesMap])

  const calendarDataByOfferAndOccupancy = useAppSelector(state => {
    const result: Record<string, Record<string, Record<string, App.Calendar>>> = {}

    for (const offer of offers) {
      const packages = offerPackagesMap[offer.id]
      if (packages.length === 0) continue

      result[offer.id] = {}

      for (const room of rooms) {
        const occupancyKey = generateOccupancyStringByRoom(room)
        const occupancyCalendars = state.calendar.calendarsByOccupancy[occupancyKey]

        if (occupancyCalendars) {
          result[offer.id][occupancyKey] = {}
          for (const pkg of packages) {
            if (occupancyCalendars[pkg.uniqueKey]) {
              result[offer.id][occupancyKey][pkg.uniqueKey] = occupancyCalendars[pkg.uniqueKey]
            }
          }
        }
      }
    }

    return result
  }, shallowEqual)

  const isCalendarLoading = useAppSelector(state => {
    if (rooms.length === 0 || offers.length === 0) return false

    const result = offers.some(offer => {
      return rooms.some(room => {
        const occupancyKey = generateOccupancyStringByRoom(room)
        const packages = offerPackagesMap[offer.id]
        return packages.some(pkg => {
          const loadingFlag = state.calendar.calendarsByOccupancyLoading[occupancyKey]?.[pkg.uniqueKey]
          const hasData = !!state.calendar.calendarsByOccupancy[occupancyKey]?.[pkg.uniqueKey]
          // Treat "not yet requested / not yet present" as loading to avoid early false negatives.
          return loadingFlag || (!hasData && loadingFlag === undefined)
        })
      })
    })

    return result
  })

  const { flightDataByFareClass, isFlightLoading } = useFlightDataByFareClass({
    rooms,
    dateRangesByMonth: filteredDateRangesByMonth,
    searchFareClass,
    currency,
    charterBundleRoute,
    restrictedFareClassesNeeded,
    seatEligiblePassengers,
    feederOriginAirportCode,
  })

  const calendarPricingByDate = useMemo(() => {
    const allDates = Object.values(filteredDateRangesByMonth).flat()

    const calendarResult: Record<string, CalendarItemBundleData> = {}
    for (const dateRange of allDates) {
      calendarResult[dateRange.checkIn] = {
        fetching: isCalendarLoading || isFlightLoading,
        available: false,
        totalPrice: 0,
        pricePerPerson: 0,
        offerCount: 0,
      }
    }

    for (const offer of offers) {
      const packages = offerPackagesMap[offer.id]
      const calendarDataByOccupancy = calendarDataByOfferAndOccupancy[offer.id]

      const fareClassPolicy = fareClassPolicyByOfferId.get(offer.id) ?? getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      if (
        selectedFareClass &&
        fareClassPolicy.allowedFareClasses.length > 0 &&
        !fareClassPolicy.allowedFareClasses.includes(selectedFareClass)
      ) {
        continue
      }
      const effectiveFareClass = selectedFareClass ?? resolveAllowedFareClass({
        selectedFareClass,
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      }) ?? searchFareClass

      if (packages.length === 0 || !calendarDataByOccupancy) {
        continue
      }

      // Build resolvedFlightByDate with fare-class-specific pricing
      const classFlightData = flightDataByFareClass[effectiveFareClass] ??
        flightDataByFareClass[searchFareClass] ??
        EmptyObject as Record<string, FlightDataByDate>
      const resolvedFlightByDate: Record<string, ResolvedFlightInfo> = {}
      for (const { checkIn } of allDates) {
        const flightData = classFlightData[checkIn]
        resolvedFlightByDate[checkIn] = {
          price: resolveFlightPriceForFareClass(flightData, effectiveFareClass, seatEligiblePassengers),
          originalPrice: resolveOriginalFlightPriceForFareClass(flightData, effectiveFareClass),
          fetching: flightData?.fetching ?? false,
          seatsLeft: flightData?.seatsLeft,
          promotionSeatsLeft: flightData?.promotionSeatsLeft,
          isPromotionPriceApplied: flightData?.isPromotionPriceApplied,
          fnfSeatsLeft: flightData?.fnfSeatsLeft,
          isFnfPriceApplied: flightData?.isFnfPriceApplied,
          hasReturnFareFree: flightData?.hasReturnFareFree,
        }
      }

      const bundleEntries = computeBundleDataForOffer({
        dateRanges: allDates,
        offer,
        rooms,
        durationPackages: packages,
        calendarDataByOccupancy,
        resolvedFlightByDate,
        totalPassengers,
        seatEligiblePassengers,
        isCalendarLoading,
        currentRegionCode,
      })

      for (const [checkIn, entry] of Object.entries(bundleEntries)) {
        const bd = entry.bundleData
        // Update calendar aggregate
        if (bd.available && bd.totalPrice > 0) {
          const calendarEntry = calendarResult[checkIn]
          if (calendarEntry) {
            calendarEntry.offerCount += 1
            calendarEntry.available = true

            if (!calendarEntry.totalPrice || bd.totalPrice < calendarEntry.totalPrice) {
              calendarEntry.totalPrice = bd.totalPrice
              calendarEntry.pricePerPerson = bd.pricePerPerson
              calendarEntry.memberTotalPrice = bd.memberTotalPrice
              calendarEntry.memberPricePerPerson = bd.memberPricePerPerson
              calendarEntry.originalTotalPrice = bd.originalTotalPrice
              calendarEntry.originalPricePerPerson = bd.originalPricePerPerson
              calendarEntry.originalMemberTotalPrice = bd.originalMemberTotalPrice
              calendarEntry.originalMemberPricePerPerson = bd.originalMemberPricePerPerson
              calendarEntry.hotelPrice = bd.hotelPrice
              calendarEntry.hotelMemberPrice = bd.hotelMemberPrice
              calendarEntry.hotelValue = bd.hotelValue
              calendarEntry.flightPrice = bd.flightPrice
              calendarEntry.roomsLeft = bd.roomsLeft
              calendarEntry.seatsLeft = bd.seatsLeft
              calendarEntry.promotionSeatsLeft = bd.promotionSeatsLeft
              calendarEntry.isPromotionPriceApplied = bd.isPromotionPriceApplied
              calendarEntry.fnfSeatsLeft = bd.fnfSeatsLeft
              calendarEntry.isFnfPriceApplied = bd.isFnfPriceApplied
              calendarEntry.hasReturnFareFree = bd.hasReturnFareFree
              calendarEntry.hotelLoyaltyCalculationRequest = bd.hotelLoyaltyCalculationRequest
            }
          }
        }
      }
    }

    // Per-date `fetching` is strictly tied to global loading — never flip it false
    // until both calendar + flight feeds settle, otherwise the lowest price visibly
    // ticks down as more offers stream in (the "flashing prices" UX bug).
    const stillLoading = isCalendarLoading || isFlightLoading
    for (const checkIn of Object.keys(calendarResult)) {
      calendarResult[checkIn].fetching = stillLoading
    }

    return calendarResult
  }, [
    offers,
    offerPackagesMap,
    calendarDataByOfferAndOccupancy,
    filteredDateRangesByMonth,
    flightDataByFareClass,
    fareClassPolicyByOfferId,
    rooms,
    totalPassengers,
    seatEligiblePassengers,
    isCalendarLoading,
    isFlightLoading,
    selectedFareClass,
    searchFareClass,
    currentRegionCode,
  ])

  // Per-cabin lowest hotel by date. Hotel pricing is cabin-agnostic *within* an offer —
  // what's cabin-specific is which offers contribute (campaignTags can restrict an offer
  // to certain cabins). Building this aggregate independently of `selectedFareClass`
  // keeps each per-cabin column in the desktop trip-options table stable as the user
  // toggles the page-level cabin filter, since the column anchors on its own cabin's
  // qualifying-offer set rather than the page-filtered set.
  const hotelPriceByCabinAndDate = useMemo<Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>>(() => {
    const allDates = Object.values(filteredDateRangesByMonth).flat()
    const result: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>> = {}
    for (const cabin of selectableCashCabins) {
      result[cabin] = {}
    }

    for (const offer of offers) {
      const packages = offerPackagesMap[offer.id]
      const calendarDataByOccupancy = calendarDataByOfferAndOccupancy[offer.id]
      if (packages.length === 0 || !calendarDataByOccupancy) continue

      const fareClassPolicy = fareClassPolicyByOfferId.get(offer.id) ?? getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      const offerCabins: ReadonlyArray<FlightsClassTypes> = fareClassPolicy.allowedFareClasses.length === 0
        ? selectableCashCabins
        : selectableCashCabins.filter(cabin => fareClassPolicy.allowedFareClasses.includes(cabin))
      if (offerCabins.length === 0) continue

      for (const { checkIn } of allDates) {
        const allocation = allocatePackagesForDate({
          offer,
          rooms,
          durationPackages: packages,
          calendarDataByOccupancy,
          checkIn,
        })
        if (!allocation.isValid || allocation.totalHotelPrice <= 0) continue

        for (const cabin of offerCabins) {
          const cabinMap = result[cabin]!
          const existing = cabinMap[checkIn]
          if (!existing || (existing.hotelPrice ?? Infinity) > allocation.totalHotelPrice) {
            cabinMap[checkIn] = {
              hotelPrice: allocation.totalHotelPrice,
              hotelMemberPrice: allocation.totalHotelMemberPrice,
              hotelValue: allocation.totalHotelValue,
            }
          }
        }
      }
    }

    return result
  }, [offers, offerPackagesMap, calendarDataByOfferAndOccupancy, filteredDateRangesByMonth, fareClassPolicyByOfferId, rooms, selectableCashCabins])

  const isLoading = isCalendarLoading || isFlightLoading

  return {
    calendarPricingByDate,
    hotelPriceByCabinAndDate,
    isLoading,
  }
}
